import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavBar from './components/TopNavBar';
import MetricCard from './components/MetricCard';
import AppointmentTimeline from './components/AppointmentTimeline';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import SystemNotifications from './components/SystemNotifications';
import NavigationPreventer from '../../components/NavigationPreventer';

import { useAuth } from '../../state/auth/useAuth';
import { 
  adminAPI, 
  reportsAPI 
} from '../../api';


const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    adminStats: {},
    appointmentStats: {},
    patientStats: {},
    inventoryStats: {},
    todaysAppointments: [],
    recentActivities: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Fetch dashboard data using single all-stats endpoint
  const fetchDashboardData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setErrors({});
      
      // Use the comprehensive all-stats endpoint
      const allStatsResponse = await reportsAPI.getDashboardSummary().catch(err => {
        console.error('Error fetching dashboard stats:', err);
        setErrors(prev => ({ ...prev, dashboard: err.message }));
        return {
          data: {
            todaysAppointments: { stats: {}, appointments: [] },
            inventory: { totalItems: 0 },
            recentActivityLogs: []
          }
        };
      });
      
      const statsData = allStatsResponse.data || {};
      
      setDashboardData({
        appointmentStats: statsData.todaysAppointments?.stats || {},
        patientStats: { todayPatients: statsData.todaysAppointments?.stats?.total || 0 },
        inventoryStats: { totalItems: statsData.inventory?.totalItems || 0 },
        todaysAppointments: statsData.todaysAppointments?.appointments || [],
        recentActivities: (statsData.recentActivityLogs || []).slice(0, 10) // Top 10 activities
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setErrors(prev => ({ ...prev, general: error.message }));
      setDashboardData({
        appointmentStats: {},
        patientStats: {},
        inventoryStats: {},
        todaysAppointments: [],
        recentActivities: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    fetchDashboardData();
  }, [user]);

  // Refresh dashboard data
  const refreshDashboard = () => {
    if (user) {
      fetchDashboardData();
    }
  };

  // Dynamic metrics based on user role
  const getMetrics = () => {
    const { appointmentStats, adminStats, patientStats, inventoryStats } = dashboardData;
    
    const baseMetrics = [
      {
        id: 1,
        title: "Today\'s Appointments",
        value: loading ? "..." : errors.appointments ? "N/A" : String(appointmentStats.today || 0),
        change: appointmentStats.today > (appointmentStats.yesterday || 0) ? `+${appointmentStats.today - (appointmentStats.yesterday || 0)}` : "0",
        changeType: appointmentStats.today > (appointmentStats.yesterday || 0) ? "positive" : "neutral",
        icon: "Calendar",
        color: "blue",
        onClick: () => navigate('/appointment-scheduling')
      }
    ];

    // Add admin-specific metrics if user is admin
    if (user?.role === 'Admin' || user?.role === 'admin') {
      baseMetrics.push(
        {
          id: 4,
          title: "Inventory Items",
          value: loading ? "..." : errors.inventory ? "N/A" : String(inventoryStats.totalItems || 0),
          change: inventoryStats.lowStockCount ? `-${inventoryStats.lowStockCount}` : "0",
          changeType: inventoryStats.lowStockCount > 0 ? "negative" : "positive",
          icon: "Package",
          color: "purple",
          onClick: () => navigate('/inventory')
        }
      );
    } else if (user?.role === 'Doctor' || user?.role === 'doctor') {
      // Doctor-specific metrics
      baseMetrics.push(
        {
          id: 2,
          title: "My Patients Today",
          value: loading ? "..." : errors.patients ? "N/A" : String(patientStats.todayPatients || appointmentStats.today || 0),
          change: "+" + (appointmentStats.completed || 0),
          changeType: "positive",
          icon: "Users",
          color: "orange",
          onClick: () => navigate('/patients')
        },
        {
          id: 3,
          title: "Completed Today",
          value: loading ? "..." : errors.appointments ? "N/A" : String(appointmentStats.completed || 0),
          change: appointmentStats.completed > 0 ? `${Math.round((appointmentStats.completed / (appointmentStats.today || 1)) * 100)}%` : "0%",
          changeType: "positive",
          icon: "CheckCircle",
          color: "green",
          onClick: () => navigate('/appointments')
        }
      );
    } else {
      // General user metrics
      baseMetrics.push({
        id: 2,
        title: "Total Appointments",
        value: loading ? "..." : errors.appointments ? "N/A" : String(appointmentStats.total || 0),
        change: "+" + (appointmentStats.scheduled || 0),
        changeType: "positive",
        icon: "Users",
        color: "orange",
        onClick: () => navigate('/appointments')
      });
    }

    return baseMetrics;
  };

  const metrics = getMetrics();


  // Transform backend activity logs for display
  const ACTIVITY_TYPE_LABELS = {
    login: 'logged in',
    logout: 'logged out',
    password_change: 'changed password',
    password_reset: 'reset password',
    create: 'created a record',
    read: 'viewed a record',
    update: 'updated a record',
    delete: 'deleted a record',
    user_create: 'created a user account',
    user_update: 'updated a user account',
    user_delete: 'deleted a user account',
    user_activate: 'activated a user account',
    user_deactivate: 'deactivated a user account',
    permission_change: 'changed permissions',
    role_change: 'changed user role',
    appointment_create: 'scheduled an appointment',
    appointment_update: 'updated an appointment',
    appointment_cancel: 'cancelled an appointment',
    patient_create: 'registered a new patient',
    patient_update: 'updated patient record',
    prescription_create: 'issued a prescription',
    prescription_update: 'updated a prescription',
    lab_report_create: 'created a lab report',
    lab_report_update: 'updated a lab report',
    emr_create: 'created a medical record',
    emr_update: 'updated a medical record',
    emr_delete: 'deleted a medical record',
    billing_create: 'created a billing record',
    billing_update: 'updated a billing record',
    invoice_create: 'created an invoice',
    invoice_update: 'updated an invoice',
    invoice_cancel: 'cancelled an invoice',
    payment_create: 'recorded a payment',
    payment_update: 'updated a payment',
    export: 'exported data',
    import: 'imported data',
    config_change: 'changed system configuration',
    availability_expire: 'marked expired availability slots',
  };

  const RESOURCE_LABELS = {
    patients: 'Patients',
    appointments: 'Appointments',
    users: 'Users',
    admin_users: 'Users',
    admin: 'Admin',
    prescriptions: 'Prescriptions',
    lab_reports: 'Lab Reports',
    billing: 'Billing',
    invoices: 'Invoices',
    payments: 'Payments',
    inventory: 'Inventory',
    medicines: 'Medicines',
    imaging_master: 'Imaging Services',
    emr: 'Medical Records',
    immunizations: 'Immunizations',
    beds: 'Beds',
    hr: 'HR',
    payroll: 'Payroll',
    hospital_settings: 'Hospital Settings',
    auth: 'Authentication',
    reports: 'Reports',
    availability: 'Availability',
    charges: 'Charges',
  };

  const isRawHttpAction = (str) => /^(GET|POST|PUT|PATCH|DELETE)\s+\//.test(str || '');

  const humanizeAction = (activityType, resource) => {
    if (ACTIVITY_TYPE_LABELS[activityType]) return ACTIVITY_TYPE_LABELS[activityType];
    // Fall back to resource + verb
    const res = RESOURCE_LABELS[resource] || (resource || '').replace(/_/g, ' ');
    return res ? `updated ${res.toLowerCase()}` : 'performed action';
  };

  const transformActivities = (activities) => {
    if (!Array.isArray(activities)) return [];
    
    return activities.map(activity => {
      const rawAction = activity.action || '';
      const activityType = activity.activityType || activity.type || 'general';
      const resource = activity.resource || '';

      // If saved action looks like raw HTTP (old data), regenerate a friendly label
      const readableAction = isRawHttpAction(rawAction)
        ? humanizeAction(activityType, resource)
        : rawAction || humanizeAction(activityType, resource);

      // Never show raw MongoDB IDs; show a readable resource area instead
      const mongoIdPattern = /^[0-9a-f]{24}$/i;
      const rawTarget = activity.resourceId || activity.resource || '';
      const readableTarget = mongoIdPattern.test(rawTarget)
        ? (RESOURCE_LABELS[resource] || resource.replace(/_/g, ' ') || 'system')
        : rawTarget.replace(/_/g, ' ') || 'system';

      return {
        id: activity._id || activity.id,
        user: activity.userName || activity.userEmail || 'Unknown User',
        action: readableAction,
        target: readableTarget,
        type: activityType,
        timestamp: activity.timestamp ? new Date(activity.timestamp) : new Date(),
        location: activity.ipAddress || activity.location || 'System',
        success: activity.success,
        userRole: activity.userRole,
        resource: resource
      };
    });
  };
  
  const recentActivities = transformActivities(dashboardData.recentActivities);


  const formatCurrentTime = () => {
    return currentTime?.toLocaleString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Navigate based on tab
    switch(tabId) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'master-data':
        navigate('/admin/dashboard');
        break;
      case 'patients':
        navigate('/users'); // Users page handles patients
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'reports':
        navigate('/accounting');
        break;
      case 'finance':
        navigate('/finance');
        break;
      case 'settings':
        navigate('/hospital-settings');
        break;
      case 'profile':
        // TODO: Add profile route
        console.log('Profile navigation - route not yet configured');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationPreventer />
      <TopNavBar 
        user={user} 
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <main className="pt-20 px-4 lg:px-6">
        <div className="space-y-6">
          {/* Welcome Header */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Welcome back, {user?.name || user?.email || 'User'}
                  </h1>
                  {loading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2D869C]"></div>
                  )}
                </div>
                {Object.keys(errors).length > 0 && (
                  <div className="text-sm text-red-600 mt-1 font-semibold">
                    Some data may be unavailable. 
                    <button 
                      onClick={refreshDashboard}
                      className="ml-2 underline hover:text-red-700 font-bold"
                      disabled={loading}
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-4 lg:mt-0 flex items-center gap-4">
                <button
                  onClick={refreshDashboard}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-semibold bg-[#2D869C]/10 text-[#2D869C] border-2 border-[#2D869C]/20 rounded-lg hover:bg-[#2D869C]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                >
                  <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                <div className="text-sm font-medium text-gray-600">
                  {formatCurrentTime()}
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-6">
            {metrics?.map((metric) => (
              <MetricCard
                key={metric?.id}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                color={metric?.color}
                onClick={metric?.onClick}
              />
            ))}
          </div>

         
          {/* Recent Activity - Full Width */}
          <div className="mt-6">
            <RecentActivity activities={recentActivities} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;